import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Download, Sparkles, Star, ChevronDown, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  PROMINENT:  { bg: "bg-emerald-500/15", ring: "border-emerald-500/40", text: "text-emerald-400" },
  MODERATE:   { bg: "bg-amber-500/10",   ring: "border-amber-500/40",   text: "text-amber-400" },
  SUPPRESSED: { bg: "bg-red-500/10",     ring: "border-red-500/40",     text: "text-red-400" },
};

const PLANET_ICONS = { Sun:"☉", Moon:"☽", Mars:"♂", Mercury:"☿", Jupiter:"♃",
                          Venus:"♀", Saturn:"♄", Rahu:"☊", Ketu:"☋" };

export default function NakshatraWisdomPanel({ profileId, profile }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/nakshatras/reading/${profileId}`, { params: { lang } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [profileId, lang]);
  useEffect(() => { load(); }, [load]);

  const toggle = (p) => setExpanded(e => ({ ...e, [p]: !e[p] }));

  return (
    <div className="space-y-4" data-testid="nakshatra-wisdom-panel">
      <div className="border-l-4 border-[#D4AF37] pl-3 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">
            Wisdom of the Nakshatras
          </div>
          <div className="text-[10px] text-[#6E6B64] font-mono mt-1">
            Every planet's nakshatra, its deity, myth, and character — expressed at your planet's own strength intensity.
            Source: your uploaded 'Wisdom of the Nakshatras' PDF · Powered by GPT-4o-mini.
          </div>
        </div>
        {data && (
          <button onClick={() => window.open(`${API}/nakshatras/reading-pdf/${profileId}`, "_blank")}
                    className="text-[11px] uppercase tracking-widest font-mono px-3 py-2 rounded-sm
                                border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F1115]
                                flex items-center gap-1.5"
                    data-testid="nak-download-pdf">
            <Download size={12} /> Download PDF
          </button>
        )}
      </div>

      {loading && (
        <div className="text-center text-[#A19E95] font-mono text-sm py-6">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Reading each planet's nakshatra…
        </div>
      )}

      {data && data.summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="nak-summary-panel">
          {/* Strong nakshatras */}
          <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star size={14} className="text-emerald-400" />
              <div className="text-xs uppercase tracking-widest font-mono text-emerald-400">
                Strong Nakshatras — {data.summary.strong_count}
              </div>
            </div>
            {data.summary.strong_count === 0 && (
              <div className="text-[10px] font-mono text-[#6E6B64] italic">
                No planets are in a prominent strength tier for this native.
              </div>
            )}
            {(data.summary.strong || []).map((s) => (
              <div key={s.planet} className="mb-3 last:mb-0 border-l-2 border-emerald-500/40 pl-3">
                <div className="text-sm text-[#E6E1D6]">
                  <b>{PLANET_ICONS[s.planet]} {s.planet}</b> · {s.nakshatra}
                  <span className="text-[10px] text-[#A8896C] ml-2">
                    lord {s.nak_lord} — {s.lord_nature}
                  </span>
                </div>
                <div className="text-[10px] text-emerald-300 mt-1">
                  + {(s.positive_qualities || []).join(" · ")}
                </div>
                <div className="text-[10px] text-[#A19E95] mt-0.5">
                  ⚠ {(s.negative_qualities || []).join(" · ")}
                </div>
              </div>
            ))}
          </div>

          {/* Weak nakshatras */}
          <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star size={14} className="text-red-400" />
              <div className="text-xs uppercase tracking-widest font-mono text-red-400">
                Weak Nakshatras — {data.summary.weak_count}
              </div>
            </div>
            {data.summary.weak_count === 0 && (
              <div className="text-[10px] font-mono text-[#6E6B64] italic">
                No planets are in a suppressed strength tier — a well-balanced chart.
              </div>
            )}
            {(data.summary.weak || []).map((s) => (
              <div key={s.planet} className="mb-3 last:mb-0 border-l-2 border-red-500/40 pl-3">
                <div className="text-sm text-[#E6E1D6]">
                  <b>{PLANET_ICONS[s.planet]} {s.planet}</b> · {s.nakshatra}
                  <span className="text-[10px] text-[#A8896C] ml-2">
                    lord {s.nak_lord} — {s.lord_nature}
                  </span>
                </div>
                <div className="text-[10px] text-red-300 mt-1">
                  ⚠ needs work: {(s.negative_qualities || []).join(" · ")}
                </div>
                <div className="text-[10px] text-[#A19E95] mt-0.5">
                  + latent gifts: {(s.positive_qualities || []).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {(data.planets || []).map(r => {
            const tier = TIER_COLORS[r.intensity_tier] || TIER_COLORS.MODERATE;
            const scorePct = Math.max(0, Math.min(100, (r.score || 0) * 10));
            const isOpen = expanded[r.planet];
            return (
              <div key={r.planet}
                     className={`border ${tier.ring} rounded-sm ${tier.bg}`}
                     data-testid={`nak-planet-${r.planet}`}>
                <button onClick={() => toggle(r.planet)}
                          className="w-full flex items-center justify-between p-4 text-left">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="text-2xl text-[#D4AF37]">{PLANET_ICONS[r.planet]}</span>
                    <div className="min-w-0">
                      <div className="text-base text-[#E6E1D6]">
                        <b>{r.planet}</b> · {r.nakshatra || "—"}
                        {r.pada ? <span className="text-[11px] font-mono text-[#8A857B]"> (pada {r.pada})</span> : null}
                      </div>
                      <div className="text-[11px] font-mono text-[#8A857B]">
                        {r.sign} · house {r.house || "?"} · deity {r.deity || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className={`text-[10px] uppercase tracking-widest font-mono ${tier.text}`}>
                        {r.intensity_tier}
                      </div>
                      <div className="text-[10px] font-mono text-[#8A857B]">
                        {r.state} · score {(r.score ?? 0).toFixed(1)}
                      </div>
                    </div>
                    <div className="w-16 h-1.5 bg-[#2A2D35] rounded-sm overflow-hidden">
                      <div className={`h-full ${tier.text.replace('text-','bg-')}`}
                             style={{ width: `${scorePct}%` }} />
                    </div>
                    {isOpen ? <ChevronDown size={14} className="text-[#8A857B]" />
                                : <ChevronRight size={14} className="text-[#8A857B]" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-[#2A2D35]/50 p-4 space-y-3 bg-[#0F1218]/40">
                    <div className="text-[10px] font-mono text-[#8A857B]">
                      <b>Symbol:</b> {r.symbol} · <b>Gana:</b> {r.gana} · <b>Lord of Nakshatra:</b> {r.nak_lord}
                    </div>
                    {r.story && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono mb-1">Myth &amp; Story</div>
                        <div className="text-sm text-[#E6E1D6] leading-relaxed italic">{r.story}</div>
                      </div>
                    )}
                    {Array.isArray(r.characteristics) && r.characteristics.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono mb-1">
                          Characteristics <span className="text-[#8A857B]">(at your {r.intensity_tier} tier)</span>
                        </div>
                        <ol className="space-y-1">
                          {r.characteristics.map((c, i) => (
                            <li key={i} className="flex gap-2 text-sm text-[#E6E1D6]">
                              <span className="text-[#D4AF37] font-mono w-5 text-right">{i+1}.</span>
                              <span>{c}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {r.guidance && (
                      <div className="border-t border-[#2A2D35]/50 pt-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono mb-1">Guidance</div>
                        <div className="text-sm text-[#C7C2B6]">{r.guidance}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
